export type ClientExamActionType =
  | "viewCertificate"
  | "retakeExam"
  | "retakeTraining"
  | "startSurvey";

// --- API Types (GET /api/client/educations/exam/me) ---

/** Query params - Backend QueryMyExamsDto */
export interface ClientMyExamsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  educationId?: string;
  status?: "ongoing" | "completed";
  sortBy?: "dueDate" | "examName" | "relatedEducationName";
  sortOrder?: "asc" | "desc";
}

// --- API Types (GET /api/client/educations/exam/me/educations) ---

export interface ClientMyExamEducationsQueryParams {
  status?: "ongoing" | "completed";
}

export interface ClientMyExamEducationItemResDto {
  educationId: string;
  educationName: string;
}

export interface ClientMyExamEducationsApiResponse {
  success?: boolean;
  status?: number;
  items?: ClientMyExamEducationItemResDto[];
  data?: ClientMyExamEducationItemResDto[];
  count?: number;
  message?: string;
}

/** Tamamlanan sınavlarda deneme durumu - Backend enum */
export enum ExamAttemptStatus {
  NOT_PARTICIPATED = 0,
  PASSED = 1,
  FAILED = 2,
}

/** Single exam item - Backend ClientMyExamItemResDto */
export interface ClientMyExamItemResDto {
  educationId: string;
  /** Tamamlanan deneme varsa sonuç drawer'ı için (GET .../exam/attempts/:attemptId/result) */
  attemptId?: string;
  /** Sertifika API için gerekli (GET .../educations/{id}/enrollments/{enrollmentId}/certificate) */
  enrollmentId?: string;
  examName: string;
  relatedEducationName: string;
  passingScore: number;
  dueDate: string | null;
  progressTime: string;
  /** Sınav süresi ilerleme yüzdesi (0–100) */
  progressTimePercentage?: number;
  answeredQuestionCount: number;
  totalQuestionCount: number;
  /** Tamamlanan sınavlarda alınan puan yüzdesi */
  scorePercentage?: number;
  /** Deneme durumları: 0=katılmadı, 1=geçti, 2=kaldı. Son eleman güncel durum. */
  examAttemptStatuses?: number[];
  /** Eğitim tipi yoklama gerektiriyor mu (ON_SITE / ONLINE) */
  attendanceRequired?: boolean;
  /** Yoklama tam mı (tüm oturumlara katıldı). Yoklama gerekmiyorsa true. */
  attendanceComplete?: boolean;
}

/** API response */
export interface ClientMyExamsApiResponse {
  success?: boolean;
  status?: number;
  data: ClientMyExamItemResDto[];
  count: number;
  message?: string;
}

export interface ClientExam {
  id: string;
  /** Tamamlanan sınavda sonuç API ile eşleşen deneme kimliği */
  attemptId?: string;
  /** Sertifika modalı için enrollmentId (backend exam/me'den döner) */
  enrollmentId?: string;
  examName: string;
  relatedEducation: string;
  passingGrade: number;
  deadline: string;
  isCompleted: boolean;
  // Ongoing
  timeElapsed?: string;
  timeTotal?: string;
  progressPercent?: number;
  progressFraction?: string; // e.g. "10/20"
  // Completed
  score?: number;
  /** Alınan puan yüzdesi (endpoint: scorePercentage) */
  scorePercentage?: number;
  /** Deneme durumları: 0=katılmadı, 1=geçti, 2=kaldı */
  examAttemptStatuses?: number[];
  /** examAttemptStatuses'e göre türetilir: 1 varsa true, yoksa son status 2 ise false */
  passed?: boolean;
  actionType?: ClientExamActionType;
  /** Eğitim tipi yoklama gerektiriyor mu */
  attendanceRequired?: boolean;
  /** Yoklama tam mı — false ise "Sınava Başla" butonu kilitlenir */
  attendanceComplete?: boolean;
}

// --- GET /api/client/educations/:educationId/enrollments/:enrollmentId/certificate ---
export interface EnrollmentCertificateData {
  id: string;
  enrollmentID: string;
  issuedAt: string;
  issuedByUserID: string;
  certificateNumber: string;
  description: string;
  certificateName: string;
  education: { id: string; name: string };
  certificateTemplateId: string;
  certificateTemplateName: string;
  parsedHtml: string;
  issuedClientCount: number;
  createdAt: string;
}

export interface EnrollmentCertificateApiResponse {
  success: boolean;
  status: number;
  data: EnrollmentCertificateData;
  message?: string;
}

// --- GET /api/client/educations/:educationId/exam (sınav çözme sayfası) ---
export interface ClientExamQuestionOptionDto {
  id: string;
  optionText: string;
  isCorrect?: boolean;
}

export interface ClientExamQuestionDto {
  id: string;
  questionText: string;
  order: number;
  allowMultipleSelection: boolean;
  imageFileID?: string | null;
  imagePreviewUrl?: string;
  options: ClientExamQuestionOptionDto[];
}

export interface ClientExamForTakingDto {
  id: string;
  name: string;
  durationMinutes: number;
  questions: ClientExamQuestionDto[];
}

// --- POST /api/client/educations/:educationId/exam/start ---
export interface ClientExamStartAttemptDto {
  id: string;
  examID: string;
  enrollmentID: string;
  userID: string;
  startedAt: string;
  endsAt: string;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Önceki soruya geri gidebilme izni (varsayılan: true) */
  canGoPreviousQuestions?: boolean;
}

// --- GET /api/client/educations/:educationId/exam/questions (start sonrası) ---
export interface ClientExamQuestionsOptionDto {
  id: string;
  questionID: string;
  optionText: string;
  order: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ClientExamQuestionsQuestionDto {
  id: string;
  examID: string;
  questionText: string;
  order: number;
  imageFileID?: string | null;
  imageUrl?: string | null;
  selectedOptionIds?: string[];
  allowMultipleSelection: boolean;
  createdAt?: string;
  updatedAt?: string;
  options: ClientExamQuestionsOptionDto[];
  image?: {
    id?: string;
    downloadUrl?: string;
    [key: string]: unknown;
  } | null;
}

export interface ClientExamQuestionsItemDto {
  attempt: ClientExamStartAttemptDto;
  questions: ClientExamQuestionsQuestionDto[];
  /** Sınav genelinde önceki soruya geri gidebilme izni (data.canGoPreviousQuestions) */
  canGoPreviousQuestions?: boolean;
}

export interface ClientExamQuestionsResponseDto {
  success?: boolean;
  status?: number;
  /** Backend bazen tek obje { attempt, questions }, bazen dizi döndürebilir */
  data: ClientExamQuestionsItemDto | ClientExamQuestionsItemDto[];
  count?: number;
  message?: string;
}

// --- GET /api/client/educations/:educationId/exam/attempts/:attemptId/result (tamamlanmış sınav özeti) ---
export interface ClientExamResultAttemptDto {
  id: string;
  examID: string;
  enrollmentID: string;
  userID: string;
  startedAt: string;
  endsAt: string;
  finishedAt: string | null;
  status: number;
  scorePercentage: number;
  createdAt: string;
  updatedAt: string;
}

export interface ClientExamResultData {
  attempt: ClientExamResultAttemptDto;
  passingScore: number;
  questions: ClientExamQuestionsQuestionDto[];
}

export interface ClientExamResultApiResponse {
  success?: boolean;
  status?: number;
  data: ClientExamResultData;
  message?: string;
}
